/**
 * Shared hook to restore form state from pending DB items on mount,
 * and auto-clear when all pending items complete.
 *
 * Used by Image Generator, Text-to-Video, Frames-to-Video, and Product Catalog.
 */

import { useEffect, useRef } from "react";

interface PendingItem {
  prompt: string | null;
  reference_image_url: string | null;
}

interface UseGenerationRestoreOptions {
  pendingItems: PendingItem[];
  isLoading: boolean;
  currentPrompt: string;
  setPrompt: (val: string) => void;
  currentImage: string | null;
  setImage: (val: string | null) => void;
  /** Skip restore (e.g. animateImage navigation) */
  skip?: boolean;
}

export function useGenerationRestore({
  pendingItems,
  isLoading,
  currentPrompt,
  setPrompt,
  currentImage,
  setImage,
  skip = false,
}: UseGenerationRestoreOptions) {
  const hasRestoredRef = useRef(false);
  const restoredValuesRef = useRef<{ prompt: string; image: string | null } | null>(null);

  useEffect(() => {
    if (isLoading || skip) return;

    // RESTORE: on first load, if form is empty and there are pending items
    if (!hasRestoredRef.current && pendingItems.length > 0) {
      const mostRecent = pendingItems[0];
      const promptToRestore = mostRecent.prompt ?? "";
      const imageToRestore = mostRecent.reference_image_url ?? null;

      const formIsEmpty = !currentPrompt && !currentImage;
      if (formIsEmpty && (promptToRestore || imageToRestore)) {
        if (promptToRestore) setPrompt(promptToRestore);
        if (imageToRestore) setImage(imageToRestore);
        restoredValuesRef.current = { prompt: promptToRestore, image: imageToRestore };
      }
      hasRestoredRef.current = true;
      return;
    }

    // CLEAR: when all pending items complete, clear only if values match what was restored
    if (hasRestoredRef.current && pendingItems.length === 0 && restoredValuesRef.current) {
      const { prompt: restoredPrompt, image: restoredImage } = restoredValuesRef.current;
      if (currentPrompt === restoredPrompt) setPrompt("");
      if (currentImage === restoredImage) setImage(null);
      restoredValuesRef.current = null;
    }
  }, [isLoading, skip, pendingItems, currentPrompt, currentImage, setPrompt, setImage]);
}
